import { FC, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { GroupedInvoiceFileProcessingStatusDto, InvoiceFileProcessingStatusDto, InvoiceUploadSignedUrlDto } from 'src/generated/services/TFinancialApi';

import IconCollapse from 'src/assets/images/icons/grid-2.svg';
import IconExpand from 'src/assets/images/icons/list.svg';

import { useUploadData } from 'src/use-cases/upload/useUploadData';
import { UploadStatusItem } from './UploadStatusItem';
import { useUploadUI } from './useUploadUI';
import { UploadSectionProvider } from './SectionProviders';
import { IGroupingItemData, IGroupingItems } from './types';
import { UploadSectionLoader } from './SectionLoader';
import { UploadSectionUnderReview } from './SectionUnderReview';
import { UploadSectionOther } from './SectionOther';
import { categorizeUploadResults } from './uploadUtils';

interface IUploadResultItems {
  processing: IGroupingItemData[];
  other: IGroupingItemData[];
  underReview: IGroupingItemData[];
  completed: IGroupingItems;
}

// Helper function to group files by invoice
const groupFilesByInvoice = (items: IGroupingItemData[]): IGroupingItemData[][] => {
  const grouped = new Map<string, IGroupingItemData[]>();

  items.forEach((item) => {
    // Use invoice ID as grouping key, fallback to filename if no invoice
    const groupKey = item.invoiceDetail?.invoiceId || item.fileName;

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, []);
    }
    grouped.get(groupKey)!.push(item);
  });

  return Array.from(grouped.values());
};

/**
 * StepUploadStatus component
 */
export const StepUploadStatus: FC = () => {
  const { uploadResults, errorProcessingStatuses, batchId } = useUploadUI();
  // Processing Data
  const { processingData: uploadProcessings } = useUploadData(batchId, true);

  const [isCollapseAll, setIsCollapseAll] = useState(false);
  const [isExpandAll, setIsExpandAll] = useState(true);

  const defaultExpanded = useMemo(() => {
    if (isExpandAll) return true;
    if (isCollapseAll) return false;
    return true;
  }, [isExpandAll, isCollapseAll]);

  const uploadResultItems: IUploadResultItems = useMemo(() => {
    const groupedItems: IGroupingItemData[] = [];

    // Map upload results to include invoice details
    uploadResults?.forEach((result: InvoiceUploadSignedUrlDto) => {
      // Find the specific InvoiceFileProcessingStatusDto that matches the dataPipelineProcessingId
      let invoiceDetail: InvoiceFileProcessingStatusDto | undefined;
      uploadProcessings?.some((itemGrouped: GroupedInvoiceFileProcessingStatusDto) => {
        // Iterate through invoiceGroups to find the file
        return itemGrouped?.invoiceGroups?.some((invoiceGroup) => {
          const foundItem = invoiceGroup?.invoiceFileProcessingStatusList?.find(
            (item: InvoiceFileProcessingStatusDto) => item?.invoiceFileProcessingId === result?.dataPipelineProcessingId
          );
          if (foundItem) {
            invoiceDetail = foundItem;
            return true;
          }
          return false;
        });
      });
      if (invoiceDetail) {
        groupedItems.push({ ...result, invoiceDetail });
      }
    });

    // Use the new categorization utility
    return categorizeUploadResults(groupedItems, errorProcessingStatuses);
  }, [uploadResults, uploadProcessings, errorProcessingStatuses]);

  return (
    <div className='relative'>
      <div className='flex items-center justify-between'>
        <div className='flex-1'>
          <h3 className='text-18 font-bold mb-4'>Upload & Processing Status: {uploadResults?.length || 0} Items</h3>
          <p className='text-14'>Feel free to close this window while invoices are being processed.</p>
        </div>

        <div className='ml-auto flex items-center gap-16'>
          <Button
            type='button'
            variant='outlined'
            color='inherit'
            onClick={() => {
              setIsCollapseAll(true);
              setIsExpandAll(false);
            }}
            sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf', backgroundColor: '#fff', gap: '12px' }}
          >
            Collapse All
            <img src={IconCollapse} alt='' />
          </Button>
          <Button
            type='button'
            variant='outlined'
            color='inherit'
            onClick={() => {
              setIsExpandAll(true);
              setIsCollapseAll(false);
            }}
            sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf', backgroundColor: '#fff', gap: '12px' }}
          >
            Expand All
            <img src={IconExpand} alt='' />
          </Button>
        </div>
      </div>

      <div className='mt-24 flex flex-col gap-24'>
        <div className='flex flex-col gap-24'>
          {Object.entries(uploadResultItems?.completed).length > 0 &&
            Object.entries(uploadResultItems?.completed).map(([serviceProviderCode, results]) => {
              const groupedResults = groupFilesByInvoice(results);
              return (
                <UploadSectionProvider key={`provider-${serviceProviderCode}`} data={results} defaultExpanded={defaultExpanded}>
                  {groupedResults.map((group, idx) => (
                    <UploadStatusItem isDisableUpload key={`group-${group[0]?.invoiceDetail?.invoiceId || group[0]?.fileName || idx}`} dataList={group} />
                  ))}
                </UploadSectionProvider>
              );
            })}

          {/* Other Section - Priority 2: Files with resolution issues + under review */}
          {uploadResultItems?.other?.length > 0 &&
            (() => {
              const groupedOther = groupFilesByInvoice(uploadResultItems.other);
              return (
                <UploadSectionOther defaultExpanded={defaultExpanded} data={uploadResultItems?.other}>
                  {groupedOther.map((group, idx) => (
                    <UploadStatusItem isDisableUpload key={`other-${group[0]?.invoiceDetail?.invoiceId || group[0]?.fileName || idx}`} dataList={group} />
                  ))}
                </UploadSectionOther>
              );
            })()}

          {/* Under Review Section - Deprecated (now included in Other section) */}
          {/* Kept for backward compatibility but should be empty */}
          {uploadResultItems?.underReview?.length > 0 &&
            (() => {
              const groupedUnderReview = groupFilesByInvoice(uploadResultItems.underReview);
              return (
                <UploadSectionUnderReview defaultExpanded={defaultExpanded} data={uploadResultItems?.underReview}>
                  {groupedUnderReview.map((group, idx) => (
                    <UploadStatusItem isDisableUpload key={`underreview-${group[0]?.invoiceDetail?.invoiceId || group[0]?.fileName || idx}`} dataList={group} />
                  ))}
                </UploadSectionUnderReview>
              );
            })()}
          {uploadResultItems?.processing?.length > 0 &&
            (() => {
              const groupedProcessing = groupFilesByInvoice(uploadResultItems.processing);
              return (
                <UploadSectionLoader data={uploadResultItems?.processing} defaultExpanded={defaultExpanded}>
                  {groupedProcessing.map((group, idx) => (
                    <UploadStatusItem key={`processing-${group[0]?.invoiceDetail?.invoiceId || group[0]?.fileName || idx}`} dataList={group} />
                  ))}
                </UploadSectionLoader>
              );
            })()}
        </div>
      </div>
    </div>
  );
};
